import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoHelpCircleOutline } from 'react-icons/io5';
import { authAPI, configAPI } from '../services/api';
import { hasCredentialCookie } from '../services/session';
import { runTelusSsoPopup } from '../services/ssoPopup';
import LanguageToggle, { getLang, setLang } from './LanguageToggle';
import HelpFeedbackModal from './HelpFeedbackModal';
import './Login.css';
import './HelpFeedbackModal.css';

const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const DEFAULT_HOSTED_DOMAIN = 'telusmd.ca';

const S = {
  en: {
    subtitle:   'Choose how you’d like to sign in',
    telus_btn:  'Login with @telus email',
    telusmd_btn:'Login with @telusmd email',
    telusmd_signing_in: 'Connecting to Google…',
    telus_note: 'For TELUS Health Care Centres clinicians with a valid TELUS email address.',
    about_prism: 'About THCC PRISM',
    about_close: 'Close',
    help:       'Help',
    sso_unconfigured: 'Google sign-in is not configured yet. Please contact your administrator.',
    sso_wrong_domain: 'Only @telusmd.ca accounts can sign in here.',
    sso_generic_err:  'Google sign-in failed. Please try again.',
    telus_unconfigured: 'TELUS sign-in is not configured yet. Please contact your administrator.',
    telus_sso_err:      'TELUS sign-in failed or you do not have access to PRISM. Please try again or contact your administrator.',
  },
  fr: {
    subtitle:   'Choisissez votre méthode de connexion',
    telus_btn:  'Connexion avec votre courriel @telus',
    telusmd_btn:'Connexion avec votre courriel @telusmd',
    telusmd_signing_in: 'Connexion à Google…',
    telus_note: 'Pour les cliniciens des Cliniques médicales TELUS Santé avec une adresse courriel TELUS valide.',
    about_prism: 'À propos de THCC PRISM',
    about_close: 'Fermer',
    help:       'Aide',
    sso_unconfigured: "La connexion Google n'est pas encore configurée. Veuillez communiquer avec votre administrateur.",
    sso_wrong_domain: 'Seuls les comptes @telusmd.ca peuvent se connecter ici.',
    sso_generic_err:  'La connexion Google a échoué. Veuillez réessayer.',
    telus_unconfigured: "La connexion TELUS n'est pas encore configurée. Veuillez communiquer avec votre administrateur.",
    telus_sso_err:      "La connexion TELUS a échoué ou vous n'avez pas accès à PRISM. Veuillez réessayer ou communiquer avec votre administrateur.",
  },
};

// TELUS "flying T" logomark reversed out of a TELUS-purple tile. Inlined rather
// than loaded as <img src="/TELUS_Flying_T_Logomark_2026_Digital_RGB.svg">: that
// asset's viewBox carries ~50% empty margin around the mark, which rendered it
// at roughly a third of the icon box and read as a faint squiggle next to
// Google's edge-to-edge "G" — and reversing the fills to white needs the paths
// in the document. The path data is the brand asset's, untouched; only the
// placement (transform) and colour are ours.
function TelusFlyingTIcon() {
  return (
    <svg className="sso-btn__icon sso-btn__icon--telus" viewBox="0 0 100 100" aria-hidden="true">
      <rect width="100" height="100" rx="23" fill="#4B286D" />
      {/* The mark occupies x 24.81–124.32, y 24.82–72.04 of the source art;
          this fits those bounds into the tile with even padding on all sides. */}
      <g transform="translate(-11.44 10.09) scale(0.824)" fill="#FFFFFF">
        <path d="M123.98,25.7l-43.18,8.6v3.01l.14-.04c28.53-6.93,43.04-11.1,43.16-11.13.16-.04.22-.17.2-.27-.02-.11-.14-.2-.31-.17"/>
        <path d="M87.74,28.1c.67-.43.86-.16.66.32-.32.77-1.78,5.45-12.65,14.37-2.84,2.32-4.85,3.63-8.94,6.05,6.69-9.37,16.45-17.84,20.93-20.74M88.87,24.88c-2.1.12-5.99,3.23-8.12,5.08-8.5,7.35-14.81,14.11-19.41,21.18-9.58,5.13-21.78,10.4-33.91,14.52l-.29.1-2.33,6.28,1.33-.52c7.71-2.94,20.58-8.46,32.25-14.59-.66,1.75-.99,3.36-.99,4.83,0,1.1.18,2.11.55,3.04.73,1.84,2.18,3.24,4.18,4.04,2.99,1.19,7.06,1.22,11.71-.19,11.87-3.6,25.96-14.65,29.03-16.85l.11-.08c.12-.09.13-.22.08-.31-.07-.1-.21-.13-.32-.06h0l-.15.09c-3.29,1.97-19.28,11.46-29.68,14.25-5.29,1.42-9.33.64-10.85-1.49-.53-.73-.78-1.65-.78-2.72,0-2.09.98-4.76,2.89-7.72,1.75-1,3.38-1.97,4.85-2.89,10.14-5.7,21.83-16.78,22.32-23.54,0-.05,0-.1,0-.15,0-.58-.25-1.18-.7-1.63-.47-.48-1.1-.73-1.78-.69Z"/>
        <polyline points="80.66 34.33 35.22 44.09 33.47 48.81 80.79 37.31 80.87 37.29 80.87 34.28 80.66 34.33"/>
      </g>
    </svg>
  );
}

// Google's standard four-colour "G" mark, used on the @telusmd button so it
// reads as a recognizable sign-in-with-Google affordance at a glance.
function GoogleGIcon() {
  return (
    <svg className="sso-btn__icon" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.9-2.26 5.36-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 0 1 9.5 24c0-1.59.27-3.13.76-4.59l-7.98-6.19A24 24 0 0 0 0 24c0 3.87.92 7.53 2.56 10.78z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.9l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

const ABOUT_PRISM_TEXT_EN = `About PRISM (Point-of-Care Real-time Intelligence System for Medicine)

Built Specifically for THCC Clinicians

PRISM integrates established **TELUS Health Care Centres** clinical protocols and decision logic — it understands our unique clinical environment. The co-pilot supports your Preventive Health Assessments, guides your clinical reasoning, accelerates research, and grounds continuing education, helping you practice efficiently with AI that works alongside your expertise.

Longevity & Preventive Focus

PRISM is grounded in **emerging and innovative science**, keeping you at the forefront of preventive medicine. It supports identifying risk trajectories early and enables true proactive intervention, complementing your clinical judgment, not replacing it. The co-pilot equips you to have deeper conversations with patients, partner on meaningful prevention strategies, and help them take ownership of their long-term health.

Canadian Data Sovereignty

Your data never leaves the country. We provide **100% Canadian data residency**, ensuring all data entered into PRISM is stored on secure Canadian servers.

Owned & Governed by TELUS Health

This isn't a third-party plugin. It is a **proprietary TELUS Health asset**. Because we own the stack, your usage directly improves our training content, making the tool smarter for your specific clinic's needs every single day. It utilizes a "closed-loop" environment where data is never used to train public models, adhering to the TELUS Trust Model.`;

const ABOUT_PRISM_TEXT_FR = `À propos de PRISM (Point-of-Care Real-time Intelligence System for Medicine)

Conçu spécifiquement pour les cliniciens des Cliniques TELUS Santé

PRISM intègre des protocoles cliniques et une logique décisionnelle établis des **Cliniques TELUS Santé**; il comprend notre environnement clinique unique. Ce copilote soutient vos évaluations en Santé préventive, guide votre raisonnement clinique, accélère la recherche et fonde la formation continue, vous aidant à exercer efficacement grâce à une IA qui travaille parallèlement à votre expertise.

Longévité et prévention

PRISM est fondé sur **une science émergente et innovante**, vous gardant à l'avant-garde de la médecine préventive. Il aide à identifier tôt les trajectoires de risque et permet une véritable intervention proactive, complétant votre jugement clinique sans le remplacer. Le copilote vous permet d'avoir des conversations plus approfondies avec les patients, de collaborer sur des stratégies de prévention significatives et de les aider à prendre en main leur santé à long terme.

Souveraineté des données canadiennes

Vos données ne quittent jamais le pays. Nous assurons une **résidence des données 100 % canadienne**, garantissant que toutes les données saisies dans PRISM sont stockées sur des serveurs canadiens sécurisés.

Propriété et gouvernance de TELUS Santé

Il ne s'agit pas d'un module d'extension tiers. C'est un **actif exclusif de TELUS Santé**. Comme nous possédons la technologie, votre utilisation améliore directement notre contenu de formation, rendant l'outil plus intelligent pour les besoins spécifiques de votre clinique au quotidien. Il utilise un environnement en « boucle fermée » où les données ne sont jamais utilisées pour entraîner des modèles publics, conformément au modèle de confiance de TELUS.`;

// Splits on **bold** markers so paragraph text can carry inline emphasis
// (headings are already bolded via the h3 render below).
function renderWithBold(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// Generate a fresh nonce for each GIS request. The same value is sent to
// Google (embedded in the issued ID token's `nonce` claim) and to the
// backend on /api/auth/google, where the two are compared.
function randomNonce() {
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// How long to wait for GIS to hand back a credential before giving up on the
// silent Google probe. Under FedCM the moment-listener no longer reports
// isNotDisplayed/isSkippedMoment reliably, so a timer is the only dependable
// way to learn that nothing is coming.
const SILENT_GOOGLE_TIMEOUT_MS = 6000;

// The same cap for a sign-in the user explicitly asked for. Longer, because a
// real account picker is on screen and being read — but not absent: without
// it, a dismissed One Tap card never calls back at all and the Help form sits
// on "Checking your sign-in…" with no way out.
const INTERACTIVE_GOOGLE_TIMEOUT_MS = 2 * 60 * 1000;

// Is there already a PRISM session on this browser? An absent CSRF cookie is
// conclusive — it is issued and cleared in lockstep with the httpOnly session
// cookie — so we only spend a round trip when one might actually exist.
async function hasPrismSession() {
  if (!hasCredentialCookie()) return false;
  try {
    // _skipAuthRedirect: a 401 here is an answer, not a session expiring.
    // Without it the interceptor would try to "sign out" the sign-in page.
    await authAPI.getCurrentUser({ _skipAuthRedirect: true });
    return true;
  } catch {
    return false;
  }
}

// Lazily inject the GIS script on first need so a TELUS-only sign-in
// doesn't pay the cost.
let gisScriptPromise = null;
function loadGisScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GIS load failed')));
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisScriptPromise = null;
      reject(new Error('GIS load failed'));
    };
    document.head.appendChild(script);
  });
  return gisScriptPromise;
}

export default function Login({ onAuthenticated } = {}) {
  const [error,        setError]        = useState('');
  const [ssoLoading,   setSsoLoading]   = useState(false);
  const [language,     setLanguage]     = useState(getLang);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  // Whether the Help form may offer an image attachment — i.e. whether this
  // browser holds a PRISM session, which the backend requires before it will
  // accept one. Re-checked every time the modal opens.
  const [helpCanAttach, setHelpCanAttach] = useState(false);
  const navigate = useNavigate();

  // The pending nonce is generated when the doctor clicks the SSO button and
  // verified by the backend. Kept in a ref so concurrent re-renders don't
  // race with the GIS callback.
  const nonceRef = useRef(null);
  // Runtime SSO config fetched from /api/config/public.
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleHostedDomain, setGoogleHostedDomain] = useState(DEFAULT_HOSTED_DOMAIN);
  // Optimistically enabled: telus_sso_enabled is static backend config (env
  // vars / secrets present or not), not something that flips at runtime, and
  // /api/auth/telus/login already re-checks it server-side on click. Gating
  // the button's disabled state on this fetch made it flash/stay greyed out
  // whenever the request was slow or transiently failed (cold start, flaky
  // network) — fixed by only ever using the response to turn it off, never
  // to hold it off by default.
  const [telusSsoEnabled, setTelusSsoEnabled] = useState(true);

  const s = S[language];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cfg = await configAPI.getPublicConfig();
        if (!mounted) return;
        setGoogleClientId(cfg?.google_client_id || '');
        if (cfg?.google_hosted_domain) setGoogleHostedDomain(cfg.google_hosted_domain);
        setTelusSsoEnabled(!!cfg?.telus_sso_enabled);
      } catch {
        // Backend unreachable or config endpoint failed — leave the TELUS
        // button in its optimistic enabled state; a genuinely unconfigured
        // backend still catches this for real at /api/auth/telus/login.
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Surface the generic error when the TELUS callback bounces back to
  // /login?error=telus_sso (bad state, denied access, no PRISM group, etc.).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'telus_sso') {
      setError(S[getLang()].telus_sso_err);
      // Strip the query param so a reload doesn't re-show the error.
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Each time the Help modal opens, work out whether an attachment is on the
  // table. A session from another tab, or one this browser already picked up
  // via the flow below, both land here.
  useEffect(() => {
    if (!showHelpModal) {
      setHelpCanAttach(false);
      return undefined;
    }
    let alive = true;
    (async () => {
      const ok = await hasPrismSession();
      if (alive) setHelpCanAttach(ok);
    })();
    return () => { alive = false; };
  }, [showHelpModal]);

  // Ask GIS for a credential and hand back whether a PRISM session came of
  // it. With autoSelect the clinician sees nothing at all, provided their
  // browser holds a Google session that has signed in to PRISM before;
  // without it they get the ordinary One Tap / account picker.
  //
  // Needs no window of its own — One Tap is not a popup — so unlike the TELUS
  // flow this does not have to be reached straight from a click.
  const runGoogleSignIn = ({ autoSelect, timeoutMs }) => new Promise((resolve) => {
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    (async () => {
      try {
        await loadGisScript();
      } catch {
        done(false);
        return;
      }
      const nonce = randomNonce();
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          hosted_domain: googleHostedDomain,
          hd: googleHostedDomain,
          nonce,
          auto_select: autoSelect,
          cancel_on_tap_outside: true,
          callback: async (response) => {
            const credential = response?.credential;
            if (!credential) {
              done(false);
              return;
            }
            try {
              await authAPI.loginWithGoogle(credential, nonce);
              // A credential can arrive after our timeout gave up — the
              // clinician took a moment over the One Tap card. The session is
              // real either way, so unlock the attachment regardless of
              // whether anyone is still waiting on this promise.
              setHelpCanAttach(true);
              done(true);
            } catch {
              done(false);
            }
          },
        });
        window.google.accounts.id.prompt((notification) => {
          // Pre-FedCM browsers tell us straight away that nothing will be
          // shown. Newer ones do not, which is what the timer covers.
          const blocked =
            (notification.isNotDisplayed && notification.isNotDisplayed())
            || (notification.isSkippedMoment && notification.isSkippedMoment());
          if (blocked) done(false);
        });
      } catch {
        done(false);
        return;
      }
      if (timeoutMs) setTimeout(() => done(false), timeoutMs);
    })();
  });

  // The Help form's "attach an image" gate. Called from the attach button
  // when this browser has no PRISM session.
  //
  // Being signed in to either provider is enough, so we ask them rather than
  // making someone who is already authenticated somewhere sign in again and
  // lose their half-written note. Both attempts here are SILENT: if neither
  // provider volunteers an answer we stop and let the user say which one they
  // use, because guessing means showing most of them the wrong login form.
  //
  // Google goes first, and not only because most PRISM clinicians are
  // @telusmd: One Tap needs no window, so it costs nothing visible when it
  // fails, where the TELUS probe has to flash a popup to ask at all.
  //
  // Resolves true, or one of the outcome names HelpFeedbackModal maps to a
  // localized message: 'popup' | 'signin' | 'failed'.
  const ensureAttachAuth = async () => {
    if (googleClientId
        && await runGoogleSignIn({ autoSelect: true, timeoutMs: SILENT_GOOGLE_TIMEOUT_MS })) {
      setHelpCanAttach(true);
      return true;
    }

    // window.open has to happen while the click's user activation is still
    // live. Chrome and Firefox hold it for five seconds, and the Google probe
    // above is capped well inside that — but if it has lapsed anyway the
    // browser blocks the window, we get 'popup_blocked', and the chooser
    // below gives the user a fresh click to spend on it.
    const telus = telusSsoEnabled
      ? await runTelusSsoPopup({ silent: true })
      : { ok: false, reason: 'disabled' };

    // ok means the callback set cookies; confirm with the server rather than
    // trusting the URL we read them off.
    if (telus.ok && await hasPrismSession()) {
      setHelpCanAttach(true);
      return true;
    }

    if (telus.reason === 'popup_blocked') return 'popup';
    // Neither provider volunteered. Hand back 'signin', which makes the modal
    // show attachProviders below — the user picks, and we sign them in for
    // real against the one they actually use.
    return 'signin';
  };

  // Offered in the Help form once the silent attempts have come up empty.
  // @telusmd first: it is how most PRISM clinicians sign in, and it was
  // putting a TELUS password form in front of all of them that made the
  // previous cut of this useless to them.
  //
  // Each entry's run() is invoked straight from its own button click, so the
  // TELUS popup opens against a fresh user activation rather than a lapsed
  // one.
  const attachProviders = [
    {
      id: 'telusmd',
      label: s.telusmd_btn,
      run: async () => {
        if (!googleClientId) return false;
        return runGoogleSignIn({
          autoSelect: false,
          timeoutMs: INTERACTIVE_GOOGLE_TIMEOUT_MS,
        });
      },
    },
    {
      id: 'telus',
      label: s.telus_btn,
      run: async () => {
        if (!telusSsoEnabled) return false;
        const result = await runTelusSsoPopup({ silent: false });
        if (!result.ok) return false;
        const ok = await hasPrismSession();
        if (ok) setHelpCanAttach(true);
        return ok;
      },
    },
  ];

  // Server-side OAuth2 redirect flow: a full-page navigation to the backend,
  // which 302s to TELUS PingID and 302s back to /chat on success. No GIS / JS
  // SDK involved (unlike the @telusmd Google flow above).
  const handleTelusClick = () => {
    setError('');
    if (!telusSsoEnabled) {
      setError(s.telus_unconfigured);
      return;
    }
    window.location.href = '/api/auth/telus/login';
  };

  const toggleLanguage = () => {
    const next = language === 'en' ? 'fr' : 'en';
    setLanguage(next);
    setLang(next);
  };

  // GIS callback fires after Google issues the ID token. We then POST it to
  // the PRISM backend for verification + session minting.
  const handleGoogleCredential = async (response) => {
    const credential = response?.credential;
    if (!credential) {
      setError(s.sso_generic_err);
      setSsoLoading(false);
      return;
    }
    try {
      await authAPI.loginWithGoogle(credential, nonceRef.current);
      nonceRef.current = null;
      if (typeof onAuthenticated === 'function') onAuthenticated();
      navigate('/chat');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 401) {
        setError(s.sso_wrong_domain);
      } else if (err.response?.status === 503) {
        setError(s.sso_unconfigured);
      } else {
        setError(detail || s.sso_generic_err);
      }
    } finally {
      setSsoLoading(false);
    }
  };

  // Re-init GIS on every click so the hosted_domain hint and nonce are fresh.
  // We drive GIS imperatively (instead of rendering Google's default button)
  // so the existing TELUS-purple styling stays in place.
  const handleTelusmdClick = async () => {
    setError('');
    if (!googleClientId) {
      setError(s.sso_unconfigured);
      return;
    }
    setSsoLoading(true);
    try {
      await loadGisScript();
    } catch {
      setError(s.sso_generic_err);
      setSsoLoading(false);
      return;
    }
    const nonce = randomNonce();
    nonceRef.current = nonce;
    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        hosted_domain: googleHostedDomain,
        hd: googleHostedDomain,
        callback: handleGoogleCredential,
        nonce,
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.prompt((notification) => {
        // If GIS can't show the One Tap prompt (cookies disabled, FedCM,
        // etc.), fall back to a popup picker so the doctor still gets a way
        // in. notification.isNotDisplayed/isSkippedMoment cover the cases
        // that need a fallback.
        const blocked =
          (notification.isNotDisplayed && notification.isNotDisplayed()) ||
          (notification.isSkippedMoment && notification.isSkippedMoment());
        if (blocked) {
          // The classic OAuth popup. Same hd enforcement; backend re-checks
          // the hd claim regardless of which UI path issued the token.
          const oauth = window.google.accounts.oauth2.initTokenClient
            ? window.google.accounts.oauth2.initTokenClient({
                client_id: googleClientId,
                scope: 'openid email profile',
                hosted_domain: googleHostedDomain,
                hd: googleHostedDomain,
                callback: () => {
                  // OAuth token client returns an access token, not an ID
                  // token — we can't use it. Surface a clear error rather
                  // than silently doing nothing.
                  setError(s.sso_generic_err);
                  setSsoLoading(false);
                },
              })
            : null;
          if (oauth) {
            oauth.requestAccessToken();
          } else {
            setError(s.sso_generic_err);
            setSsoLoading(false);
          }
        }
      });
    } catch {
      setError(s.sso_generic_err);
      setSsoLoading(false);
    }
  };

  // If the doctor reloads /login while a stale GIS prompt is open, clean up.
  useEffect(() => {
    return () => {
      if (window.google?.accounts?.id?.cancel) {
        try { window.google.accounts.id.cancel(); } catch { /* noop */ }
      }
    };
  }, []);

  return (
    <div className="login-page">

      {/* EN/FR toggle — top-left */}
      <div className="login-lang-bar">
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      {/* Help — top-right */}
      <div className="login-help-bar">
        <button
          type="button"
          className="login-help-btn"
          onClick={() => setShowHelpModal(true)}
          aria-label={s.help}
          title={s.help}
        >
          <IoHelpCircleOutline className="login-help-btn__icon" aria-hidden="true" />
          <span className="login-help-btn__label">{s.help}</span>
        </button>
      </div>

      {/* TELUS Health logo — top center */}
      <div className="login-logo-bar">
        <img
          src={language === 'fr' ? '/TELUS_Sante_Cliniques_FR_Vert_2021_Digital_RGB.svg' : '/TELUS_Health_CareCentres_EN_Vert_2021_Digital_RGB.svg'}
          alt={language === 'fr' ? 'TELUS Santé' : 'TELUS Health'}
          className="login-logo"
        />
      </div>

      {/* Left: sweet pea illustration, bottom-anchored */}
      <div className="login-left" aria-hidden="true">
        <img
          src="/SweetPea_Purple_SideviewStem_BH_LR_RGB_0.jpg"
          alt=""
          className="login-iris"
        />
      </div>

      {/* Right: SSO chooser */}
      <div className="login-right">
        <div className="login-card">

          <h1 className="login-title">THCC PRISM</h1>
          <p className="login-subtitle">{s.subtitle}</p>

          {error && <div className="login-error">{error}</div>}

          <div className="sso-options">
            <button
              type="button"
              className="sso-btn sso-btn--telus"
              onClick={handleTelusClick}
              disabled={!telusSsoEnabled || ssoLoading}
              autoFocus
            >
              <TelusFlyingTIcon />
              <span className="sso-btn__title">{s.telus_btn}</span>
            </button>

            <button
              type="button"
              className="sso-btn sso-btn--google"
              onClick={handleTelusmdClick}
              disabled={ssoLoading}
            >
              <GoogleGIcon />
              <span className="sso-btn__title">
                {ssoLoading ? s.telusmd_signing_in : s.telusmd_btn}
              </span>
            </button>
          </div>

          <p className="login-note">{s.telus_note}</p>

          <button
            type="button"
            className="btn-about-prism"
            onClick={() => setShowAboutModal(true)}
          >
            {s.about_prism}
          </button>
        </div>
      </div>

      <HelpFeedbackModal
        open={showHelpModal}
        canAttach={helpCanAttach}
        onEnsureAttachAuth={ensureAttachAuth}
        attachProviders={attachProviders}
        onClose={() => setShowHelpModal(false)}
        defaultEmail=""
        language={language}
      />

      {showAboutModal && (
        <div
          className="about-modal-overlay"
          onClick={() => setShowAboutModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={s.about_prism}
        >
          <div className="about-modal" onClick={(e) => e.stopPropagation()}>
            <div className="about-modal-header">
              <h2>{s.about_prism}</h2>
              <button
                type="button"
                className="about-modal-close"
                onClick={() => setShowAboutModal(false)}
                aria-label={s.about_close}
              >
                ✕
              </button>
            </div>
            <div className="about-modal-body">
              {(language === 'fr' ? ABOUT_PRISM_TEXT_FR : ABOUT_PRISM_TEXT_EN).split('\n\n').map((para, i) => {
                const isHeading = i > 0 && para.split('\n').length === 1 && !para.endsWith('.');
                if (isHeading) {
                  return <h3 key={i} className="about-section-heading">{para}</h3>;
                }
                return <p key={i} className="about-paragraph">{renderWithBold(para)}</p>;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
